import { useState, useCallback, useMemo } from "react";

export default function useAccountSelection({ idKey = "id" } = {}) {
  // Map keyed by account id → account object for O(1) lookups
  const [selectedMap, setSelectedMap] = useState(new Map());
  // Mirror of ServerTable's allSelected + deselectedIds pattern:
  // selecting all is instant — no fetch needed until save time.
  const [allSelected, setAllSelected] = useState(false);
  const [deselectedIds, setDeselectedIds] = useState(new Set());

  // Derive the array only when the map changes (needed for save payload & count)
  const selectedAccounts = useMemo(
    () => [...selectedMap.values()],
    [selectedMap],
  );

  const getId = (row) => row[idKey];

  const isRowSelected = (row) => {
    if (allSelected) return !deselectedIds.has(getId(row));
    return selectedMap.has(getId(row));
  };

  const toggleRowSelection = (row) => {
    const id = getId(row);
    if (allSelected) {
      setDeselectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    } else {
      setSelectedMap((prev) => {
        const next = new Map(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.set(id, row);
        }
        return next;
      });
    }
  };

  // Select all accounts in the full dataset (no fetch needed)
  const selectAll = () => {
    setAllSelected(true);
    setDeselectedIds(new Set());
    setSelectedMap(new Map());
  };

  const deselectAll = () => {
    setAllSelected(false);
    setDeselectedIds(new Set());
    setSelectedMap(new Map());
  };

  // Batch-select a list of accounts (used for search-scoped "select all").
  // Works correctly in both allSelected and individual-selection modes.
  const selectAccounts = (accounts) => {
    if (!accounts?.length) return;
    setSelectedMap((prev) => {
      const next = new Map(prev);
      for (const account of accounts) {
        next.set(getId(account), account);
      }
      return next;
    });
    setDeselectedIds((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set(prev);
      let changed = false;
      for (const account of accounts) {
        if (next.has(getId(account))) {
          next.delete(getId(account));
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  };

  // Batch-deselect a list of accounts (used for search-scoped "deselect all").
  const deselectAccounts = (accounts) => {
    if (!accounts?.length) return;
    setSelectedMap((prev) => {
      const next = new Map(prev);
      let changed = false;
      for (const account of accounts) {
        if (next.has(getId(account))) {
          next.delete(getId(account));
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    setDeselectedIds((prev) => {
      const next = new Set(prev);
      for (const account of accounts) {
        next.add(getId(account));
      }
      return next;
    });
  };

  // Used when pre-populating selection (e.g. editing an existing group)
  const initializeSelection = (accounts) => {
    setSelectedMap(new Map(accounts.map((a) => [getId(a), a])));
    setAllSelected(false);
    setDeselectedIds(new Set());
  };

  const resetSelection = useCallback(() => {
    setSelectedMap(new Map());
    setAllSelected(false);
    setDeselectedIds(new Set());
  }, []);

  return {
    selectedAccounts,
    allSelected,
    deselectedIds,
    isRowSelected,
    toggleRowSelection,
    selectAll,
    deselectAll,
    selectAccounts,
    deselectAccounts,
    initializeSelection,
    resetSelection,
  };
}
